import style from "./VacancyCardInfo.module.scss";
import SkeletonField from "@/components/AuthComponents/VacancyCard/Skeleton/SkeletonField";
import Markers from "@/components/BaseComponents/Markers";
import {
  useResearcherAllQuery,
  useRecruiterAllQuery,
  useVacationCategoriesQuery,
  useVacationGetFileInfoMutation,
  vacationCustomApi,
  useVacationCardQuery,
} from "@/core/api/vacancy/vacancy";
import {
  InformationCell,
  MultiselectData,
  Skeleton,
  Text,
  UploadedFile,
} from "@aurora/components";
import { getFormattedFileSize } from "@aurora/components/dist/FileUploader/helpers";
import cx from "classnames";
import { FC, useEffect, useState, useMemo } from "react";

interface Props {
  vacancyId?: string;
}

interface Participants {
  id: string;
  fullName: string;
  email: string;
}

const VacancyCardInfo: FC<Props> = ({ vacancyId }: any) => {
  const [fileVacancy, changeFileVacancy]: any = useState<File | null>();
  const [fileVacancyInfo, changeFileInfo]: any = useState(null);
  const [labels, setLabels] = useState<MultiselectData[]>([]);
  const [isParticipantsVacancy, setParticipantsVacancy] = useState(false);
  const [recruiter, setRecruiter] = useState<Participants[]>([]);
  const [researcher, setResearcher] = useState<Participants[]>([]);

  const { data: CardInfoData, isLoading: CardInfoIsLoading } =
    useVacationCardQuery(
      {
        vacancyId,
      },
      {
        skip: !vacancyId,
      },
    );
  const {
    data: CategoriesData,
    error: CategoriesError,
    isLoading: isCategoriesLoading,
  } = useVacationCategoriesQuery();
  const {
    data: RecruiterData,
    error: RecruiterError,
    isLoading: isRecruiterLoading,
  } = useRecruiterAllQuery();
  const {
    data: ResearcherData,
    error: ResearcherError,
    isLoading: isResearcherLoading,
  } = useResearcherAllQuery();
  const [getFileInfo] = useVacationGetFileInfoMutation();

  useEffect(() => {
    if (CardInfoData) {
      if (CardInfoData?.labels.length > 0) {
        const labelsMap: MultiselectData[] = [...CardInfoData.labels];
        setLabels(labelsMap);
      }
      if (
        CardInfoData.managerId ||
        CardInfoData.recruiterId ||
        CardInfoData.researcherIds.length > 0
      ) {
        setParticipantsVacancy(true);
      }
    }
  }, [CardInfoData]);
  // отображение ошибки в консоли
  useEffect(() => {
    if (CategoriesError) console.error("CategoriesError", CategoriesError);
  }, [CategoriesError]);

  useEffect(() => {
    if (
      RecruiterData &&
      !isRecruiterLoading &&
      !RecruiterError &&
      CardInfoData
    ) {
      const newRecruiter = RecruiterData.filter(
        r => r.id === CardInfoData.recruiterId,
      ) as Participants[];
      setRecruiter(newRecruiter);
    }
  }, [RecruiterData, isRecruiterLoading, RecruiterError, CardInfoData]);
  useEffect(() => {
    if (
      ResearcherData &&
      !isResearcherLoading &&
      !ResearcherError &&
      CardInfoData
    ) {
      const newResearcher = ResearcherData.filter(r =>
        CardInfoData.researcherIds.includes(r.id),
      ) as Participants[];
      setResearcher(newResearcher);
    }
  }, [ResearcherData, isResearcherLoading, ResearcherError, CardInfoData]);

  useEffect(() => {
    if (!CardInfoData || !CardInfoData.fileUploaded) return;
    Promise.all([
      vacationCustomApi.downloadFile({ vacancyId: CardInfoData.id }),
      getFileInfo({ vacancyId: CardInfoData.id }),
    ]).then(values => {
      const file = values[0];
      const info: any = values[1];
      const vacancyFile = new File([file.data], info.data.fileName);
      changeFileInfo(info.data);
      changeFileVacancy(vacancyFile);
    });
  }, []);

  const loading = useMemo(
    () =>
      isResearcherLoading ||
      isRecruiterLoading ||
      isCategoriesLoading ||
      CardInfoIsLoading,
    [
      isResearcherLoading,
      isRecruiterLoading,
      isCategoriesLoading,
      CardInfoIsLoading,
    ],
  );

  function openPdfInNewTab() {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(fileVacancy);
    link.download = fileVacancyInfo.fileName;
    link.dispatchEvent(new MouseEvent("click"));
  }
  const [currentCategory, setCurrentCategory] = useState<{
    label?: any;
    value?: any;
  }>({});
  useEffect(() => {
    if (CardInfoData && CategoriesData) {
      setCurrentCategory(
        CategoriesData.filter(
          (cat: any) => cat.id === CardInfoData.categoryId,
        )[0],
      );
    }
  }, [CardInfoData, CategoriesData]);

  const [filteredVacancyData, setFilteredVacancyData] = useState<
    { label: any; value: any }[]
  >([]);

  useEffect(() => {
    if (CardInfoData && currentCategory)
      setFilteredVacancyData(
        [
          {
            label: "Название вакансии",
            value: CardInfoData.title,
          },
          {
            label: "Ссылка на вакансию",
            value: CardInfoData.link ? (
              <a
                className={cx("link")}
                href={CardInfoData.link}
                rel="noreferrer"
                target="_blank"
              >
                Перейти
              </a>
            ) : null,
          },
          {
            label: "Категория",
            value: currentCategory.value,
          },
          {
            label: "Контакты нанимающего менеджера",
            value: CardInfoData.managerContacts,
          },
          {
            label: "Дата создания",
            value: CardInfoData.createDate,
          },
          {
            label: "Дней в работе",
            value: String(CardInfoData.inProgressDays) || "-",
          },
          {
            label: "Локация",
            value: CardInfoData.location,
          },
        ].filter(i => i.value),
      );
  }, [CardInfoData, currentCategory]);

  //если данных нет, то не отображаем соответствующий блок
  // const filteredVacancyData = vacancyData.filter(item => item.value);

  return (
    <>
      {loading ? (
        <SkeletonField count={8} />
      ) : (
        <div>
          <div className={cx("border-bottom pb-16")}>
            <div className={style.VacationCardInfo}>
              {filteredVacancyData.map((item, idx) => (
                <InformationCell
                  key={idx}
                  label={
                    <Text tag="span" type="labelRegular">
                      {item.label}
                    </Text>
                  }
                  separator={false}
                  title={item.value}
                />
              ))}
            </div>
          </div>
          {CardInfoData?.id ? (
            <div className="pb-16 px-8">
              <Markers
                entity={"vacancy"}
                initLabels={labels}
                needConfirmed={true}
                needSaveBt={true}
                onLabelsChange={setLabels}
                vacancyId={CardInfoData?.id}
              />
            </div>
          ) : null}
          {isParticipantsVacancy ? (
            <div className={cx("border-bottom pb-16")}>
              <div className={style.VacationCardInfo}>
                <InformationCell
                  key={"pv-1"}
                  label={
                    <Text tag="span" type="labelRegular">
                      Нанимающий менеджер
                    </Text>
                  }
                  separator={false}
                  title={CardInfoData?.managerFullName || "-"}
                />
                {recruiter.length ? (
                  <InformationCell
                    key={"pv-2"}
                    label={
                      <Text tag="span" type="labelRegular">
                        {"Рекрутер"}
                        {recruiter.length > 1 ? "ы" : ""}
                      </Text>
                    }
                    separator={false}
                    title={recruiter.map((r, i) => (
                      <span key={i}>{r.fullName}</span>
                    ))}
                  />
                ) : null}
                {researcher.length ? (
                  <>
                    <InformationCell
                      key={"pv-3"}
                      label={
                        <Text tag="span" type="labelRegular">
                          {"Рессерчер"}
                          {researcher.length > 1 ? "ы" : ""}
                        </Text>
                      }
                      separator={false}
                      title={researcher.map((r, i) => (
                        <span key={i}>{r.fullName}</span>
                      ))}
                      variant={"info"}
                    />
                  </>
                ) : null}
              </div>
            </div>
          ) : null}
          {CardInfoData?.requirements ? (
            <div className="mt-16">
              <InformationCell
                label={
                  <Text tag="span" type="labelRegular">
                    Требования к кандидату
                  </Text>
                }
                separator={false}
                title={
                  <span className="formatted-text">
                    {CardInfoData?.requirements}
                  </span>
                }
              />
            </div>
          ) : null}
          {CardInfoData?.responsibilities ? (
            <div>
              <InformationCell
                label={
                  <Text tag="span" type="labelRegular">
                    Задачи вакансии
                  </Text>
                }
                separator={false}
                title={
                  <span className="formatted-text">
                    {CardInfoData?.responsibilities}
                  </span>
                }
              />
            </div>
          ) : null}
          {CardInfoData?.description ? (
            <div>
              <InformationCell
                label={
                  <Text tag="span" type="labelRegular">
                    Описание вакансии для шаблона-приглашения
                  </Text>
                }
                separator={false}
                title={
                  <span className="formatted-text">
                    {CardInfoData?.description}
                  </span>
                }
              />
            </div>
          ) : null}
          {CardInfoData?.options ? (
            <div className="pb-16 border-bottom">
              <InformationCell
                label={
                  <Text tag="span" type="labelRegular">
                    Условия вакансии
                  </Text>
                }
                separator={false}
                title={
                  <span className="formatted-text">
                    {CardInfoData?.options}
                  </span>
                }
              />
            </div>
          ) : null}
          {CardInfoData?.fileUploaded ? (
            <div className="pt-16 border-top">
              <InformationCell
                label={
                  <Text tag="span" type="labelRegular">
                    Загруженные файлы
                  </Text>
                }
                separator={false}
                title=""
              />
              {fileVacancy ? (
                <a
                  className={"text-decoration-none"}
                  onClick={() => {
                    openPdfInNewTab();
                  }}
                >
                  {" "}
                  <UploadedFile
                    description={getFormattedFileSize(fileVacancy.size)}
                    errorDescription="error description"
                    file={fileVacancy}
                    indent={false}
                    loadingDescription="loading"
                    shape="cardCell"
                    shortDescription="short"
                    showDeleteButton={false}
                    status="FILE_SUCCESS"
                    title="Файл вакансии"
                  />
                </a>
              ) : (
                <div>
                  <Skeleton
                    height={64}
                    radius="r5"
                    shape="line"
                    variant="primary"
                  />
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </>
  );
};

export default VacancyCardInfo;
